import {
    Spinner,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    TableColumnDefinition,
    TableColumnSizingOptions,
} from '@fluentui/react-components';
import { useConfigurationTableStyles } from './ConfigurationTable.styles';
import React from 'react';
import PaginationBar from '../../../PaginationBar';
import { IPagination, LoadStatus } from '../../../../model';
import {
    defaultColumnSizingOptions,
    IConfigRow,
} from './ConfigurationTable.utils';
import SegmentedBar from '../../../SegmentedBar/SegmentedBar';

export interface ConfigurationTableProps<R extends IConfigRow> {
    items: R[];
    columns: TableColumnDefinition<R>[];
    pagination?: IPagination;
    bottomActions?: React.ReactNode;
    footerRow?: React.ReactNode;
    columnSizingOptions?: TableColumnSizingOptions;
    loadStatus?: LoadStatus;
    ariaLabel?: string;
    scrollContainerRef?: React.Ref<HTMLDivElement>;
    children?: React.ReactNode;
}

function ConfigurationTable<R extends IConfigRow = IConfigRow>({
    items,
    columns,
    pagination,
    bottomActions,
    footerRow,
    columnSizingOptions,
    loadStatus,
    ariaLabel,
    scrollContainerRef,
    children,
}: ConfigurationTableProps<R>): React.ReactElement {
    const styles = useConfigurationTableStyles();

    return (
        <div className={styles.root}>
            <>
                    <div ref={scrollContainerRef} className={styles.tableScrollContainer}>
                        <DataGrid
                            className={styles.dataGrid}
                            items={items}
                            columns={columns}
                            getRowId={(item) => item.id}
                            sortable
                            resizableColumns
                            columnSizingOptions={
                                columnSizingOptions ?? defaultColumnSizingOptions
                            }
                            aria-label={ariaLabel}
                        >
                            <DataGridHeader>
                                <DataGridRow>
                                    {({ columnId, renderHeaderCell }) => (
                                        <DataGridHeaderCell key={columnId}>
                                            {renderHeaderCell()}
                                        </DataGridHeaderCell>
                                    )}
                                </DataGridRow>
                            </DataGridHeader>
                            <DataGridBody>
                                {({ item, rowId }) => (
                                    <DataGridRow key={rowId}>
                                        {({ columnId, renderCell }) => (
                                            <DataGridCell key={columnId}>
                                                {renderCell(item)}
                                            </DataGridCell>
                                        )}
                                    </DataGridRow>
                                )}
                            </DataGridBody>
                            {footerRow && (
                                <DataGridRow key="footer-row">
                                    {({ columnId }) =>
                                        columnId === columns[0]?.columnId ? (
                                            <DataGridCell
                                                style={{
                                                    gridColumn: `1 / span ${columns.length}`,
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {footerRow}
                                            </DataGridCell>
                                        ) : null
                                    }
                                </DataGridRow>
                            )}
                        </DataGrid>
                    </div>

                    {pagination && (
                        <PaginationBar
                            className={styles.paginationSection}
                            currentPage={pagination.currentPage}
                            pageSize={pagination.pageSize}
                            totalItems={pagination.totalCount}
                            actions={bottomActions}
                        />
                    )}
                    {!pagination && bottomActions && (
                        <SegmentedBar
                            className={styles.paginationSection}
                            segments={[{ node: bottomActions }]}
                        />
                    )}
                    {children}
            </>

            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </div>
    );
}

export default ConfigurationTable;
